<template>
  <div v-if="selectionCount" class="mb-4">
    <template v-if="! currentOperation">
      <div class="text-center mt-6 mb-3"><b>{{ $t('SELECTION.SELECTION_OPERATION') }}</b></div>
      <div class="d-flex flex-wrap gap-4">
        <div v-for="operation in operations" :key="operation.id" style="width: calc(33.33% - 11px)">
          <v-card-clickable rounded :disabled="operation.id == 'macro' && ! hasMacroTasks" class="flex-grow-1 bg-lightgrey pa-3 pb-2" @click="selectOperation(operation)">
            <div><img :src="operation.img" style="width: 100%"></div>
            <div class="text-center">{{ operation.title }}</div>
          </v-card-clickable>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="mt-6 d-flex align-center">
        <v-btn-icon size="x-small" class="mr-1" @click="currentOperation = null"><v-icon>far fa-arrow-left</v-icon></v-btn-icon>
        <b>{{ currentOperation.title }}</b>
      </div>
      <div class="text-medium-emphasis line-height-normal pre mb-2">{{ currentOperation.description }}</div>
      <div v-if="currentOperation.id == 'progress'">
        <div class="d-flex align-center">
          <div style="flex: 1 1 100%">
            <v-progress-linear class="pointer" :model-value="progressSelected" color="successgreen" height="16" rounded
                               @click="clickProgressBar($event)">
              <div v-show="progressSelected > 0" :style="{ 'margin-right': `${100 - progressSelected}%` }"
                   class="text-white small flex-center-center" style="height: 100%; transition: all .3s">
                {{ progressSelected }}%
              </div>
            </v-progress-linear>
          </div>
          <v-text-field v-model.number="progressSelected" type="number" min="0" max="100" step="10" suffix="%"
                        class="ml-2" style="min-width: max-content">
          </v-text-field>
        </div>
        <div class="my-4 text-center"><v-btn color="primary" @click="applyProgress">{{ $t('GLOBAL.APPLY') }}</v-btn></div>
      </div>
      <div v-else-if="currentOperation.id == 'step'">
        <v-select v-model="stepSelected" :items="availableProcessSteps" item-title="label" item-value="id"
                  :placeholder="$t('KANBAN.IN_BACKLOG')" clearable>
        </v-select>
        <div class="my-4 text-center"><v-btn color="primary" @click="applyStep">{{ $t('GLOBAL.APPLY') }}</v-btn></div>
      </div>
      <div v-else-if="currentOperation.id == 'color'" class="color-picker">
        <color-picker-color v-model:color="colorData.color" v-model:color-shade="colorData.colorShade" :target="colorData.dummyEl"
                            :planning="planning" disable-renaming @selected="applyColor">
        </color-picker-color>
      </div>
      <div v-else-if="currentOperation.id == 'macro'">
        <select-element v-model="macroSelectedElement" :items="availableMacroElements" :placeholder="$t('SELECTION.MACRO_CREATE_MACRO')"></select-element>
        <div class="my-4 text-center"><v-btn color="primary" @click="applyMacro">{{ $t('GLOBAL.APPLY') }}</v-btn></div>
      </div>
      <div v-else-if="currentOperation.id == 'duplicate'">
        <div class="my-4 text-center"><v-btn color="primary" @click="applyDuplicate">{{ $t('GLOBAL.APPLY') }}</v-btn></div>
      </div>
      <div v-else-if="currentOperation.id == 'delete'">
        <div class="my-4 text-center"><v-btn color="primary" @click="applyDelete">{{ $t('GLOBAL.APPLY') }}</v-btn></div>
      </div>
    </template>
  </div>
</template>

<script>
  import { computed, reactive } from 'vue';
  import { mapActions } from 'pinia';
  import useUiPlanningStore from '@/stores/ui/planning';
  import useSelectionStore from '@/stores/selection';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import usePlanningElementsStore from '@/stores/planning/elements';
  import PlanningElement from '@/models/PlanningElement';
  import { useMacroParents } from '@/components/ElementDetails/Macro/useMacroParents';
  import { useMacroElement } from '@/components/ElementDetails/Macro/useMacroElement';
  import SelectElement from '@/components/Reusables/SelectElement.vue';
  import ColorPickerColor from '@/components/ColorPicker/ColorPickerColor.vue';
  import selectionProgressImg from './img/selection-progress.png';
  import selectionStepImg from './img/selection-step.png';
  import selectionColorImg from './img/selection-color.png';
  import selectionMacroImg from './img/selection-macro.png';
  import selectionDuplicateImg from './img/selection-duplicate.png';
  import selectionDeleteImg from './img/selection-delete.png';

  export default {
    components: {
      SelectElement,
      ColorPickerColor,
    },
    props: {
      planning: { type: Object, required: true },
    },
    emits: ['hide-selection-elements'],
    setup(props) {
      const planning = reactive(props.planning);
      const elements = computed(() => planning.elements);
      const { getMacroParentsSet } = useMacroParents(elements);
      const { updateMacroElementOnce } = useMacroElement(planning);
      return {
        getMacroParentsSet,
        updateMacroElementOnce,
      };
    },
    data() {
      return {
        operations: [
          { id: 'progress', title: this.$t('ELEMENT-DETAIL.PROGRESS'), img: selectionProgressImg, description: this.$t('SELECTION.PROGRESS_DESCRIPTION') },
          { id: 'step', title: this.$t('KANBAN.STEP'), img: selectionStepImg, description: this.$t('SELECTION.STEP_DESCRIPTION') },
          { id: 'color', title: this.$t('COLORPICKER.COLOR'), img: selectionColorImg, description: this.$t('SELECTION.COLOR_DESCRIPTION') },
          { id: 'macro', title: this.$t('GLOBAL.MACRO_BUBBLE'), img: selectionMacroImg, description: this.$t('SELECTION.MACRO_DESCRIPTION') },
          { id: 'duplicate', title: this.$t('PLANNING.DUPLICATE'), img: selectionDuplicateImg, description: this.$t('SELECTION.DUPLICATE_DESCRIPTION') },
          { id: 'delete', title: this.$t('GLOBAL.DELETE'), img: selectionDeleteImg, description: this.$t('SELECTION.DELETE_DESCRIPTION') },
        ],
        currentOperation: null,
        progressSelected: 100,
        stepSelected: null,
        colorData: { color: null, colorShade: null, dummyEl: new PlanningElement(this.planning, { type: 'task', color: null }) },
        macroSelectedElement: null,
        uiPlanningStore: useUiPlanningStore(),
        selectionStore: useSelectionStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      selectionCount() {
        return this.selectionStore.selectionCount;
      },
      availableProcessSteps() {
        return this.planning.process.steps.filter(step => step.id);
      },
      availableMacroElements() {
        return this.planning.elements.filter(el => el.isType('macro'));
      },
      hasMacroTasks() { return this.usersAccessRightStore.hasMacroTasks; },
    },
    methods: {
      selectOperation(operation) {
        this.$emit('hide-selection-elements');
        this.currentOperation = operation;
      },
      clickProgressBar(event) {
        const fullWidth = event.currentTarget.clientWidth;
        if (! fullWidth) return;
        this.progressSelected = Math.min(Math.round(event.offsetX / fullWidth * 10) * 10, 100);
      },
      applyProgress() {
        this.startChangingElement();
        this.selectionStore.selection.forEach((elId) => {
          const el = this.planning.elements.find(item => item.id == elId);
          if (! el || ! el.isType('task', 'milestone')) return;
          el.setProgress(this.progressSelected || 0);
        });
        this.changingElement();
        this.currentOperation = null;
      },
      applyStep() {
        this.startChangingElement();
        this.selectionStore.selection.forEach((elId) => {
          const el = this.planning.elements.find(item => item.id == elId);
          if (! el || ! el.isType('task', 'milestone')) return;
          el.setProcessStep(this.stepSelected);
        });
        this.changingElement();
        this.currentOperation = null;
      },
      applyColor() {
        if (typeof this.colorData.color !== 'number') return;
        this.startChangingElement();
        this.selectionStore.selection.forEach((elId) => {
          const el = this.planning.elements.find(item => item.id == elId);
          if (! el || ! el.isType('task', 'macro')) return;
          el.setColorId(this.colorData.color);
          el.setColorShadeId(this.colorData.colorShade);
        });
        this.changingElement();
        this.currentOperation = null;
      },
      applyMacro() {
        this.startChangingElement();
        let macroEl = this.macroSelectedElement;
        if (! macroEl) {
          // create new macro
          const $tableWrapper = document.getElementById('table-wrapper') || document.body;
          const newElements = this.uiPlanningStore.addElement({
            type: 'macro',
            x: 0,
            y: $tableWrapper.offsetTop + window.scrollY + $tableWrapper.clientHeight / 2, // center of visible area
            silent: true,
          });
          macroEl = newElements?.[0];
        }
        if (! macroEl) return;
        const elParentsIds = this.getMacroParentsSet(macroEl.id);

        const macroSubTasks = new Set(macroEl.getSubTasks() || []);
        this.selectionStore.selection.forEach((elId) => {
          if (elParentsIds.has(elId)) return; // skip parents
          macroSubTasks.add(elId);
        });
        macroEl.setSubTasks([...macroSubTasks]);

        // compute dates in case macro is not on screen (macroElement mixin apply only on visible elements)
        this.updateMacroElementOnce(macroEl);

        this.changingElement(macroEl);

        this.currentOperation = null;
      },
      applyDuplicate() {
        this.selectionStore.duplicateSelection();
        this.currentOperation = null;
      },
      applyDelete() {
        this.selectionStore.deleteSelection();
        this.currentOperation = null;
      },
      ...mapActions(usePlanningElementsStore, ['changingElement', 'startChangingElement']),
    },
  };
</script>
